/*
 * WebCRD
 * Web to print solution that automates ordering, fulfillment, job ticketing, production management and chargebacks across corporate print centers.
 * Copyright 1999-2026 Rochester Software Associates (service@rocsoft.com)
 */

import { createSlice } from '@reduxjs/toolkit';

import { changePositionTag, getTags as getTagsAPI, renameTag } from '~/api/common/tags/tags';
import createSimpleAPIAsyncThunk from '~common-store/helpers/createSimpleAPIAsyncThunk';
import deepFreeze from '~utils/objects/deepFreeze';

import { merge } from '../helpers/mergeUpdates';
import { createConsolidatedUpdateAsyncThunk } from '../helpers/spamProtection';


export const getTags = createSimpleAPIAsyncThunk('tags/getTags', getTagsAPI);


const { thunk: updateTag } = createConsolidatedUpdateAsyncThunk(
    'tags/updateName',
    {
        // Incoming change, update state immediately
        onQueued: ({ id, changes }, { dispatch }) => {
            dispatch(addPendingTagsUpdate({ id, changes }));
        },

        // Next, send the request to the server
        updateCall: async ({ changes }) => {
            if (changes.name !== undefined) {
                return renameTag(changes.id, changes.name);
            } else if (changes.position !== undefined) {
                return changePositionTag(changes.id, changes.position);
            }
        },

        // Server responds with the final change in the database, in case it rejected something
        // or altered it on the server side
        // That response will be handled in our reducer below
        onUpdate: ({ response, appliedChanges, pendingChanges, resolve }) => {
            resolve({
                updatesFromServer: response,
                appliedChanges,
                pendingChanges,
            });
        },
        // when merging changes, use this helper merge function
        merge,
    }
);
export { updateTag };


const initialState = deepFreeze({
    list: [],
});

const tagsSlice = createSlice({
    name: 'tags',
    initialState,
    reducers: {
        // Assume the incoming tag name changes are fine and update the state locally before we even send to the server
        addPendingTagsUpdate: (state, action) => {
            const { changes } = action.payload;
            // Merging into an array, the changes also need to be an array
            merge(state.list, [changes]);
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getTags.fulfilled, (state, action) => {
            state.list = action.payload;
        });

        // Incoming response is being handled here
        // Take the final response from the server, merge and overwrite our client state with that tag
        builder.addCase(updateTag.fulfilled, (state, action) => {
            const { updatesFromServer, pendingChanges } = action.payload;
            if (updatesFromServer.singleTag) {
                // Single update, merge into the list
                merge(state.list, [updatesFromServer.singleTag]);
                if (pendingChanges) {
                    merge(state.list, [pendingChanges]);
                }
            } else {
                // Entire list sent back, replace entire state
                // When we change positions we need the array elements to be in different positions
                // A simple change of elements in the object wont do
                state.list = updatesFromServer.tags;
            }
        });
    },
});

const { addPendingTagsUpdate } = tagsSlice.actions;

export default tagsSlice.reducer;
